import { Accordion, Typography, AccordionSummary, Stack, AccordionDetails } from '@mui/material';
import { VyIcon } from '@vy/ui-design-system';
import type { FC } from 'react';
import { useToggle } from '../../../../../../../../../../../hooks/use-toggle';
import { isEmptyArray, isNullOrUndefined } from '@vy/utils';
import type { ItemOf, Optional } from '@vy/utils';
import { formatPrice } from '@vy/services';
import { formatDate } from '../../../../../../../../../../../utils/date';
import type { GetLightInvoicesResponse, GetExportedIntegrationsResponse } from '@vy/api-definition';
import { XeroInvoiceDetailsEmptyState } from './xero-invoice-details-accordion-components/XeroInvoiceDetailsEmptyState';
import { SYNC_JOB_STATUSES } from '@vy/integrations-types';

type XeroInvoiceDetailsAccordionProps = {
  xeroIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>;
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>;
};

export const XeroInvoiceDetailsAccordion: FC<XeroInvoiceDetailsAccordionProps> = ({
  xeroIntegration,
  invoice,
}) => {
  const [expanded, toggleExpanded] = useToggle(true);
  const invoiceDetails = getInvoiceDetails(xeroIntegration, invoice);

  return (
    <Accordion expanded={expanded} onChange={() => toggleExpanded()} sx={{ p: 0 }}>
      <AccordionSummary
        expandIcon={
          <VyIcon
            icon="ArrowDown"
            style={{
              color: '#323232',
            }}
          />
        }
        sx={{ p: 0, height: '34px', alignItems: 'center' }}
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            gap: 1
          }}>
          <VyIcon icon="InvoiceList" />
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              lineHeight: "24px"
            }}>
            Invoice Details
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {isEmptyArray(invoiceDetails) ? (
          <XeroInvoiceDetailsEmptyState />
        ) : (
          <Stack
            direction="column"
            style={{
              backgroundColor: '#F5F6FA',
              borderRadius: 1,
            }}
            sx={{
              gap: "24px",
              padding: "12px 20px",
              mb: 1.5,
              mt: 1.5,
              alignItems: "flex-start"
            }}>
            {invoiceDetails.map((detail) => (
              <Stack
                key={detail.label}
                direction="column"
                sx={{
                  alignItems: "flex-start",
                  gap: "2px"
                }}>
                <Typography
                  sx={{
                    lineHeight: "20px",
                    fontSize: 14
                  }}>
                  {detail.label}
                </Typography>
                <Typography
                  sx={{
                    lineHeight: "20px",
                    fontSize: 14,
                    color: "#7E8796"
                  }}>
                  {detail.value}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

function getInvoiceDetails(
  xeroIntegration: Optional<ItemOf<GetExportedIntegrationsResponse>>,
  invoice: ItemOf<GetLightInvoicesResponse['invoices']>,
) {
  if (isNullOrUndefined(xeroIntegration) || isXeroFailedSync(xeroIntegration)) {
    return [];
  }

  const EMPTY_DATA = '--';

  return [
    {
      label: 'Invoice amount',
      value: formatPrice(invoice.revenueBreakdown.total, invoice.currency),
    },
    {
      label: 'Xero Invoice ID',
      value: xeroIntegration.integrationExternalId ?? EMPTY_DATA,
    },
    {
      label: 'Posting date',
      value: isNullOrUndefined(invoice.issuedAt) ? EMPTY_DATA : formatDate(invoice.issuedAt),
    },
    {
      label: 'Due date',
      value: isNullOrUndefined(invoice.dueDate) ? EMPTY_DATA : formatDate(invoice.dueDate),
    },
    {
      label: 'Export date',
      value: isNullOrUndefined(xeroIntegration.createdAt)
        ? EMPTY_DATA
        : formatDate(xeroIntegration.createdAt),
    },
  ];
}

function isXeroFailedSync(xeroIntegration: ItemOf<GetExportedIntegrationsResponse>) {
  return xeroIntegration?.status === SYNC_JOB_STATUSES.FAILED;
}
